// src/features/media/components/VideoFilterChips.tsx

import { Button, Group, Pill } from '@mantine/core';
import { MediaUtils } from '../MediaUtils';
import { formatDate } from '@/shared/utils/date';
import type { VideoFilters } from '@/lib/types/Media';

interface VideoFilterChipsProps {
  filters: VideoFilters;
  onChange: (patch: Partial<VideoFilters>) => void;
  onClearAll: () => void;
}

type TagBucket = 'tagsAll' | 'tagsAny' | 'tagsNone';

const TAG_BUCKET_LABELS: Record<TagBucket, string> = {
  tagsAll: 'Has all',
  tagsAny: 'Has any',
  tagsNone: 'Excludes',
};

function durationChipLabel(min: number | null, max: number | null): string {
  if (min !== null && max !== null) {
    return `Duration ${MediaUtils.formatTimecode(min)}–${MediaUtils.formatTimecode(max)}`;
  }
  if (min !== null) {
    return `Duration ≥ ${MediaUtils.formatTimecode(min)}`;
  }
  if (max !== null) {
    return `Duration ≤ ${MediaUtils.formatTimecode(max)}`;
  }
  return '';
}

/**
 * Removable chips for each active advanced filter, plus "Clear all"
 * (which also resets the free-text search — D5). Visible even when the
 * filter panel is collapsed so active filters are never hidden state.
 */
export function VideoFilterChips({ filters, onChange, onClearAll }: VideoFilterChipsProps) {
  function removeTag(bucket: TagBucket, tag: string): void {
    onChange({ [bucket]: filters[bucket].filter((t) => t !== tag) });
  }

  return (
    <Group gap="xs">
      {(Object.keys(TAG_BUCKET_LABELS) as TagBucket[]).flatMap((bucket) =>
        filters[bucket].map((tag) => (
          <Pill key={`${bucket}-${tag}`} withRemoveButton onRemove={() => removeTag(bucket, tag)}>
            {`${TAG_BUCKET_LABELS[bucket]}: ${tag}`}
          </Pill>
        ))
      )}

      {filters.publishedAfter !== null && (
        <Pill withRemoveButton onRemove={() => onChange({ publishedAfter: null })}>
          {`Published ≥ ${formatDate(filters.publishedAfter)}`}
        </Pill>
      )}
      {filters.publishedBefore !== null && (
        <Pill withRemoveButton onRemove={() => onChange({ publishedBefore: null })}>
          {`Published < ${formatDate(filters.publishedBefore)}`}
        </Pill>
      )}

      {(filters.durationMin !== null || filters.durationMax !== null) && (
        <Pill withRemoveButton onRemove={() => onChange({ durationMin: null, durationMax: null })}>
          {durationChipLabel(filters.durationMin, filters.durationMax)}
        </Pill>
      )}

      {filters.state !== null && (
        <Pill withRemoveButton onRemove={() => onChange({ state: null })}>
          {filters.state === 'active' ? 'State: Active' : 'State: Inactive'}
        </Pill>
      )}

      {filters.needsAttention && (
        <Pill withRemoveButton onRemove={() => onChange({ needsAttention: false })}>
          Needs attention
        </Pill>
      )}

      <Button variant="subtle" size="compact-xs" onClick={onClearAll}>
        Clear all
      </Button>
    </Group>
  );
}
